// ZK Attendance Platform - API Client

import {
  clearAuthStorage,
  getAccessToken,
  getStoredTenantId,
  hasStoredUser,
  isAuthSessionEnded,
  persistAuthSession,
} from './auth-storage';
import { CSRF_PROTECTION_HEADER, CSRF_PROTECTION_VALUE } from './csrf';
import { isTenantScopeExcludedPath } from '@/contexts/SuperAdminTenantContext';
import { isAuthExemptPublicRoute } from '@/lib/i18n/public-serbian-routes';
import { 
  User,
  ReferencePhotoProfileState,
  ReferencePhotoOpenRequest, 
  Tenant, 
  Device, 
  Attendance, 
  AttendanceEventDto, 
  CreateUserDto, 
  UpdateUserDto,
  PagedResult,
  UserPagedResult,
  ApiResponse,
  LoginRequest,
  RefreshTokenRequest,
  LoginResponse,
  AuthUser,
  RegisterCredentials,
  TenantInfo,
  DashboardMetrics,
  TenantMetrics,
  UserFilters,
  TenantStats,
  AttendanceFilters,
  DeviceFilters,
  RealTimeConfig,
  RealTimeStatus,
  UserSettings,
  UpdateUserSettings,
  TenantFeatures,
  Department,
  DepartmentPagedResult,
  DepartmentListItem,
  CreateDepartmentDto,
  UpdateDepartmentDto,
  BulkSetDepartmentResult,
  WorkerClient,
  WorkerOfflineIncident,
  WorkerStatistics,
  UpdateWorkerDto,
  IntegrationClientListItem,
  CreateIntegrationClientRequest,
  CreateIntegrationClientResponse,
  PatchIntegrationClientRequest,
  RotateIntegrationKeyResponse,
  CreateFeedbackRequest,
  FeedbackDetail,
  FeedbackPagedResult,
  FeedbackComment,
  FeedbackAttachment,
  PatchFeedbackRequest,
  HelpArticleCsat,
  PlatformHelpAnalytics,
  PlatformFeedbackAnalytics,
} from './types';
import {
  extractApiErrorCode,
  extractApiErrorMessage,
  throwApiResponseError,
} from './i18n/translate-api-error';

export const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'https://localhost:44396';

/**
 * Build a user-facing message from API error JSON.
 * Supports our `{ message }` envelope and ASP.NET Core ProblemDetails / ValidationProblemDetails (`title`, `errors`, `detail`).
 */
export function parseApiErrorMessage(errorData: unknown, httpStatus: number): string {
  return extractApiErrorMessage(errorData, httpStatus);
}

class ApiClient {
  private baseUrl: string;
  private refreshInFlight: Promise<string> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Generate a unique Correlation ID for request tracing
   * Format: UUID v4
   */
  private generateCorrelationId(): string {
    // Simple UUID v4 generator
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }




  public async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers: Record<string, string> = {};
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Do not attach tenant/session headers to anonymous auth calls — a stale JWT can confuse
    // downstream auth handling and makes failed-login UX harder to reason about.
    const skipSessionHeaders =
      endpoint === '/api/auth/login' ||
      endpoint === '/api/auth/register' ||
      endpoint === '/api/auth/forgot-password' ||
      endpoint === '/api/auth/reset-password' ||
      endpoint === '/api/public/leads';

    // JWT AUTH: Bearer from in-memory access token (refresh is HttpOnly cookie)
    if (typeof window !== 'undefined' && !skipSessionHeaders) {
      const token = getAccessToken();
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const pathname = window.location.pathname;
      const skipTenantHeader = skipSessionHeaders || isTenantScopeExcludedPath(pathname);

      if (!skipTenantHeader) {
        const tenantId = getStoredTenantId();
        if (tenantId) {
          headers['X-Tenant-ID'] = tenantId;
        }
      }
    }

    // Add Correlation ID for distributed tracing
    const correlationId = this.generateCorrelationId();
    headers['X-Correlation-ID'] = correlationId;
    if (typeof window !== 'undefined') {
      (window as unknown as { __lastCorrelationId?: string }).__lastCorrelationId = correlationId;
    }

    if (endpoint === '/api/auth/refresh' || endpoint === '/api/auth/logout') {
      headers[CSRF_PROTECTION_HEADER] = CSRF_PROTECTION_VALUE;
    }

    // Add any additional headers from options
    if (options.headers) {
      Object.assign(headers, options.headers);
    }

    let response: Response;
    try {
      response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });
    } catch (networkError) {
      // Aborted fetches are expected on filter/tenant race; do not wrap as NETWORK_ERROR.
      if (
        (typeof DOMException !== 'undefined' &&
          networkError instanceof DOMException &&
          networkError.name === 'AbortError') ||
        (networkError instanceof Error && networkError.name === 'AbortError')
      ) {
        throw networkError;
      }
      const detail =
        networkError instanceof Error ? networkError.message : 'Failed to fetch';
      throw new Error(
        parseApiErrorMessage({ errorCode: 'NETWORK_ERROR', message: detail }, 0)
      );
    }

    if (!response.ok) {
      // Handle 401 Unauthorized - try to refresh token (once)
      if (
        response.status === 401 &&
        !isRetry &&
        endpoint !== '/api/auth/login' &&
        endpoint !== '/api/auth/refresh' &&
        endpoint !== '/api/auth/logout'
      ) {
        const canRefresh =
          typeof window !== 'undefined' &&
          !isAuthSessionEnded() &&
          (!!getAccessToken() || hasStoredUser());

        if (!canRefresh) {
          throw new Error(
            parseApiErrorMessage({ errorCode: 'SESSION_EXPIRED' }, 401)
          );
        }

        try {
          await this.refreshToken();
          // Retry the original request with new token
          return this.request<T>(endpoint, options, true);
        } catch (refreshError) {
          if (!isAuthSessionEnded()) {
            console.error('❌ Token refresh failed:', refreshError);
          }
          if (typeof window !== 'undefined') {
            clearAuthStorage();
            // Marketing/auth pages: clear quietly — do not bounce visitors to /login.
            if (!isAuthExemptPublicRoute(window.location.pathname)) {
              window.location.href = '/login';
            }
          }
          throw new Error(
            parseApiErrorMessage({ errorCode: 'SESSION_EXPIRED' }, 401)
          );
        }
      }

      const errorData = await response.json().catch(() => ({}));
      let errorMessage = parseApiErrorMessage(errorData, response.status);
      if (endpoint === '/api/auth/login' && response.status === 401) {
        const code = extractApiErrorCode(errorData) ?? 'INVALID_CREDENTIALS';
        errorMessage = parseApiErrorMessage(
          {
            ...(typeof errorData === 'object' && errorData !== null
              ? (errorData as Record<string, unknown>)
              : {}),
            errorCode: code,
          },
          response.status
        );
      }

      // Handle 403 Subscription Expired
      if (response.status === 403 && errorData.error === 'SUBSCRIPTION_EXPIRED') {
        console.error('❌ Subscription expired:', errorData);
        // Show subscription expired modal/page
        if (typeof window !== 'undefined') {
          // Store subscription error for display
          localStorage.setItem('subscription_error', JSON.stringify(errorData));
          // Don't logout, just block access
        }
        throw new Error(errorMessage);
      }

      // Expected auth/validation/concurrency failures — UI shows the message; avoid Next.js console error overlay
      const errorCode = extractApiErrorCode(errorData);
      const isQuietClientError =
        response.status === 400 ||
        response.status === 409 ||
        (response.status === 403 && errorCode === 'SHIFTS_FEATURE_DISABLED') ||
        (response.status === 401 &&
          (endpoint === '/api/auth/login' || endpoint === '/api/auth/refresh')) ||
        endpoint === '/api/public/leads';
      if (!isQuietClientError) {
        console.error('API Request failed:', {
          url,
          status: response.status,
          statusText: response.statusText,
          errorMessage,
          headers: headers
        });
      }

      throw Object.assign(new Error(errorMessage), {
        errorCode: errorCode ?? undefined,
        status: response.status,
      });
    }

    return response.json();
  }

  // Authentication
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    clearAuthStorage();

    const response = await this.request<ApiResponse<LoginResponse>>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    if (response.success && response.data) {
      if (typeof window !== 'undefined') {
        persistAuthSession({
          token: response.data.token,
          user: response.data.user,
          tenantId: response.data.tenantId,
        });
      }
      return (response as any).data;
    }

    const loginMsg =
      typeof response.message === 'string' && response.message.trim()
        ? response.message.trim()
        : extractApiErrorMessage({ errorCode: 'INVALID_CREDENTIALS' }, 401);
    throw new Error(loginMsg);
  }

  async logout(): Promise<void> {
    if (typeof window !== 'undefined' && !isAuthSessionEnded()) {
      try {
        await fetch(`${this.baseUrl}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            [CSRF_PROTECTION_HEADER]: CSRF_PROTECTION_VALUE,
          },
          credentials: 'include',
          body: JSON.stringify({}),
        });
      } catch {
        // Best-effort revoke — always clear local session
      }
    }
    clearAuthStorage();
  }

  async refreshToken(): Promise<string> {
    const run = async () => {
      if (this.refreshInFlight) {
        return this.refreshInFlight;
      }

      this.refreshInFlight = this.performRefreshToken().finally(() => {
        this.refreshInFlight = null;
      });
      return this.refreshInFlight;
    };

    if (typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function') {
      return navigator.locks.request('zk-attendance-auth-refresh', run);
    }

    return run();
  }

  private async performRefreshToken(): Promise<string> {
    if (isAuthSessionEnded() && !hasStoredUser()) {
      throw new Error(extractApiErrorMessage({ errorCode: 'SESSION_EXPIRED' }, 401));
    }

    const refreshRequest: RefreshTokenRequest = {
      token: getAccessToken() || '',
    };

    const attempt = async (): Promise<string> => {
      const response = await this.request<ApiResponse<LoginResponse>>('/api/auth/refresh', {
        method: 'POST',
        body: JSON.stringify(refreshRequest),
      });

      if (response.success && response.data) {
        persistAuthSession({
          token: response.data.token,
          user: response.data.user,
          tenantId: response.data.tenantId,
        });
        return response.data.token;
      }

      throw new Error(extractApiErrorMessage({ errorCode: 'LOGIN_FAILED' }, 401));
    };

    try {
      return await attempt();
    } catch (error) {
      // Concurrent rotation from another tab — cookie may already be rotated; retry once.
      const code =
        typeof error === 'object' && error !== null && 'errorCode' in error
          ? String((error as { errorCode?: string }).errorCode ?? '')
          : '';
      const status =
        typeof error === 'object' && error !== null && 'status' in error
          ? Number((error as { status?: number }).status)
          : 0;
      const message = error instanceof Error ? error.message : '';
      const busy =
        code === 'REFRESH_TOKEN_BUSY' ||
        status === 409 ||
        /already in progress|retry/i.test(message);
      if (!busy) throw error;
      await new Promise((r) => setTimeout(r, 150));
      return attempt();
    }
  }

  async submitSalesLead(payload: {
    firstName: string;
    lastName: string;
    email: string;
    company: string;
    phone?: string;
    employeeRange?: string;
    locationCount: string;
    terminalCount: string;
    currentSolution: string;
    message?: string;
    preferredLanguage?: string;
  }): Promise<void> {
    const response = await this.request<ApiResponse<object>>('/api/public/leads', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (!response.success) {
      throw new Error(
        typeof response.message === 'string' && response.message.trim()
          ? response.message.trim()
          : extractApiErrorMessage(response, 400)
      );
    }
  }

  // User Management
  async getUsers(filters: UserFilters = {}): Promise<UserPagedResult> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const response = await this.request<ApiResponse<UserPagedResult>>(`/api/UserManagement?${params}`);
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getUser(userId: string): Promise<User> {
    const response = await this.request<ApiResponse<User>>(`/api/UserManagement/${userId}`);
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'USER_NOT_FOUND');
  }

  async getUserErvProfile(
    userId: string,
    options: import('@/lib/types/user-erv-profile').GetUserErvProfileOptions = {}
  ): Promise<import('@/lib/types/user-erv-profile').UserErvProfile> {
    const params = new URLSearchParams();
    if (options.from) params.set('from', options.from);
    if (options.to) params.set('to', options.to);
    if (options.includeShifts === false) params.set('includeShifts', 'false');
    const qs = params.toString();
    const path = `/api/UserManagement/${encodeURIComponent(userId)}/erv-profile${qs ? `?${qs}` : ''}`;
    const response = await this.request<ApiResponse<import('@/lib/types/user-erv-profile').UserErvProfile>>(path);
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'USER_NOT_FOUND');
  }

  async getReferencePhotoCircleBlob(userId: string): Promise<Blob | null> {
    return this.fetchOptionalJpeg(`/api/users/${encodeURIComponent(userId)}/reference-photo`);
  }

  async getReferencePhotoOriginalBlob(userId: string): Promise<Blob | null> {
    return this.fetchOptionalJpeg(`/api/users/${encodeURIComponent(userId)}/reference-photo/original`);
  }

  async getOwnReferencePhotoBlob(): Promise<Blob | null> {
    return this.fetchOptionalJpeg('/api/profile/reference-photo');
  }

  async setReferencePhoto(userId: string, jpeg: Blob): Promise<{ firstPlacement: boolean }> {
    const form = new FormData();
    form.append('file', jpeg, 'photo.jpg');
    const response = await this.request<ApiResponse<{ firstPlacement: boolean }>>(
      `/api/users/${encodeURIComponent(userId)}/reference-photo`,
      { method: 'PUT', body: form }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async removeReferencePhoto(userId: string): Promise<void> {
    const response = await this.request<ApiResponse<boolean>>(
      `/api/users/${encodeURIComponent(userId)}/reference-photo`,
      { method: 'DELETE' }
    );
    if (!response.success) {
      throwApiResponseError(response, 200, 'OPERATION_FAILED');
    }
  }

  async getOwnReferencePhotoState(): Promise<ReferencePhotoProfileState> {
    const response = await this.request<ApiResponse<ReferencePhotoProfileState>>(
      '/api/profile/reference-photo/state'
    );
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getOwnReferencePhotoProposalBlob(): Promise<Blob | null> {
    return this.fetchOptionalJpeg('/api/profile/reference-photo/proposal');
  }

  async submitOwnReferencePhoto(jpeg: Blob): Promise<ReferencePhotoProfileState> {
    const form = new FormData();
    form.append('file', jpeg, 'photo.jpg');
    const response = await this.request<ApiResponse<ReferencePhotoProfileState>>(
      '/api/profile/reference-photo/requests',
      { method: 'POST', body: form }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async withdrawOwnReferencePhoto(): Promise<ReferencePhotoProfileState> {
    const response = await this.request<ApiResponse<ReferencePhotoProfileState>>(
      '/api/profile/reference-photo/requests/withdraw',
      { method: 'POST' }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getReferencePhotoRequest(userId: string): Promise<ReferencePhotoOpenRequest | null> {
    const response = await this.request<ApiResponse<ReferencePhotoOpenRequest | null>>(
      `/api/users/${encodeURIComponent(userId)}/reference-photo/request`
    );
    if (response.success) {
      return response.data ?? null;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getReferencePhotoProposalBlob(userId: string): Promise<Blob | null> {
    return this.fetchOptionalJpeg(`/api/users/${encodeURIComponent(userId)}/reference-photo/proposal`);
  }

  async approveReferencePhoto(userId: string, proposalVersion: number): Promise<void> {
    const response = await this.request<ApiResponse<boolean>>(
      `/api/users/${encodeURIComponent(userId)}/reference-photo/request/approve`,
      { method: 'POST', body: JSON.stringify({ proposalVersion }) }
    );
    if (!response.success) {
      throwApiResponseError(response, 200, 'OPERATION_FAILED');
    }
  }

  async rejectReferencePhoto(
    userId: string,
    proposalVersion: number,
    reasonCode: string,
    note?: string
  ): Promise<void> {
    const response = await this.request<ApiResponse<boolean>>(
      `/api/users/${encodeURIComponent(userId)}/reference-photo/request/reject`,
      { method: 'POST', body: JSON.stringify({ proposalVersion, reasonCode, note }) }
    );
    if (!response.success) {
      throwApiResponseError(response, 200, 'OPERATION_FAILED');
    }
  }

  private async fetchOptionalJpeg(endpoint: string): Promise<Blob | null> {
    const headers: Record<string, string> = {};
    if (typeof window !== 'undefined') {
      const token = getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
      const tenantId = getStoredTenantId();
      if (tenantId && !isTenantScopeExcludedPath(window.location.pathname)) {
        headers['X-Tenant-ID'] = tenantId;
      }
    }
    headers['X-Correlation-ID'] = this.generateCorrelationId();
    headers['Cache-Control'] = 'no-store';

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      headers,
      credentials: 'include',
      cache: 'no-store',
    });
    if (response.status === 404) return null;
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw Object.assign(new Error(parseApiErrorMessage(errorData, response.status)), {
        status: response.status,
      });
    }
    return response.blob();
  }

  async createUser(userData: CreateUserDto): Promise<User> {
    const response = await this.request<ApiResponse<User>>('/api/UserManagement', {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'USER_CREATE_FAILED');
  }

  // Tenant Admin Management
  async createTenantAdmin(tenantId: string, adminData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    phone?: string;
    department?: string;
    position?: string;
  }): Promise<User> {
    const requestData = {
      tenantId,
      userId: `admin-${tenantId}-${crypto.randomUUID().slice(0, 8)}`, // Auto-generate user ID
      firstName: adminData.firstName,
      lastName: adminData.lastName,
      email: adminData.email,
      password: adminData.password,
      phone: adminData.phone,
      department: adminData.department || 'Administration',
      position: adminData.position || 'Tenant Administrator',
      role: 'TenantAdmin',
      status: 'Active'
    };

    const response = await this.request<ApiResponse<User>>('/api/UserManagement/tenant-admin', {
      method: 'POST',
      body: JSON.stringify(requestData),
    });
    
    if (!response.success) {
      throwApiResponseError(response, 200, 'USER_CREATE_FAILED');
    }
    
    return response.data!;
  }

  async getTenantAdmins(tenantId: string): Promise<User[]> {
    const response = await this.request<ApiResponse<User[]>>(`/api/UserManagement/tenant/${tenantId}/admins`);
    
    if (!response.success) {
      throwApiResponseError(response, 200, 'OPERATION_FAILED');
    }
    
    return response.data || [];
  }

  async updateUser(userId: string, userData: UpdateUserDto): Promise<User> {
    const response = await this.request<ApiResponse<User>>(`/api/UserManagement/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });

    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'USER_UPDATE_FAILED');
  }

  async changeUserRole(
    userId: string,
    body: import('./types').ChangeUserRoleRequest
  ): Promise<import('./types').ChangeUserRoleResult> {
    const response = await this.request<ApiResponse<import('./types').ChangeUserRoleResult>>(
      `/api/UserManagement/${userId}/role`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getUserRoleChanges(
    userId: string,
    take = 20
  ): Promise<import('./types').UserRoleChangeEntry[]> {
    const response = await this.request<ApiResponse<import('./types').UserRoleChangeEntry[]>>(
      `/api/UserManagement/${userId}/role-changes?take=${take}`
    );
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async deleteUser(userId: string): Promise<ApiResponse<void>> {
    const response = await this.request<ApiResponse<void>>(`/api/UserManagement/${userId}`, {
      method: 'DELETE',
    });

    return response;
  }


  async bulkSyncUsers(userIds: string[], deviceId: string): Promise<any> {
    const response = await this.request<ApiResponse<any>>('/api/UserManagement/bulk-sync', {
      method: 'POST',
      body: JSON.stringify({ userIds, deviceId }),
    });

    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async bulkSetDepartment(
    userIds: string[],
    departmentId: string | null
  ): Promise<BulkSetDepartmentResult> {
    const response = await this.request<ApiResponse<BulkSetDepartmentResult>>(
      '/api/UserManagement/bulk-set-department',
      {
        method: 'POST',
        body: JSON.stringify({ userIds, departmentId }),
      }
    );

    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'BULK_SET_DEPARTMENT_FAILED');
  }

  async syncAllUsersToDevice(deviceId: string): Promise<any> {
    const response = await this.request<ApiResponse<any>>(`/api/UserManagement/sync-all/${deviceId}`, {
      method: 'POST',
    });

    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getUnsyncedUsers(): Promise<User[]> {
    const response = await this.request<ApiResponse<User[]>>('/api/UserManagement/unsynced');
    
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getUserSyncStatus(userId: string): Promise<any[]> {
    const response = await this.request<ApiResponse<any[]>>(`/api/UserManagement/${userId}/sync-status`);
    
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async searchUsers(searchTerm: string): Promise<User[]> {
    const response = await this.request<ApiResponse<User[]>>(`/api/UserManagement/search?searchTerm=${encodeURIComponent(searchTerm)}`);
    
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getUsersByDepartment(department: string): Promise<User[]> {
    const response = await this.request<ApiResponse<User[]>>(`/api/UserManagement/department/${encodeURIComponent(department)}`);
    
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getUsersByStatus(status: string): Promise<User[]> {
    const response = await this.request<ApiResponse<User[]>>(`/api/UserManagement/status/${status}`);
    
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async updateUserFingerprint(userId: string, fingerprintData: any): Promise<boolean> {
    const response = await this.request<ApiResponse<boolean>>(`/api/UserManagement/${userId}/fingerprint`, {
      method: 'PUT',
      body: JSON.stringify(fingerprintData),
    });
    
    if (response.success && response.data !== undefined) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'USER_UPDATE_FAILED');
  }

  async updateUserFaceTemplate(userId: string, faceTemplateData: any): Promise<boolean> {
    const response = await this.request<ApiResponse<boolean>>(`/api/UserManagement/${userId}/face-template`, {
      method: 'PUT',
      body: JSON.stringify(faceTemplateData),
    });
    
    if (response.success && response.data !== undefined) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'USER_UPDATE_FAILED');
  }

  async getUsersOnDevice(deviceId: string): Promise<User[]> {
    const response = await this.request<ApiResponse<User[]>>(`/api/UserManagement/device/${deviceId}`);
    
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async removeUserFromDevice(userId: string, deviceId: string): Promise<boolean> {
    const response = await this.request<ApiResponse<boolean>>(`/api/UserManagement/${userId}/device/${deviceId}`, {
      method: 'DELETE',
    });
    
    if (response.success && response.data !== undefined) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  // Assignment Matrix
  async getAssignedUsersOnDevice(
    deviceId: string,
    options?: { search?: string; page?: number; pageSize?: number }
  ): Promise<PagedResult<User>> {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.page != null) params.set('page', String(options.page));
    if (options?.pageSize != null) params.set('pageSize', String(options.pageSize));
    const qs = params.toString();
    const path = `/api/assignments/device/${encodeURIComponent(deviceId)}/users${qs ? `?${qs}` : ''}`;
    const response = await this.request<ApiResponse<PagedResult<User>>>(path);
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getAvailableUsersForDevice(
    deviceId: string,
    options?: { search?: string; page?: number; pageSize?: number }
  ): Promise<PagedResult<User>> {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.page != null) params.set('page', String(options.page));
    if (options?.pageSize != null) params.set('pageSize', String(options.pageSize));
    const qs = params.toString();
    const path = `/api/assignments/device/${encodeURIComponent(deviceId)}/available-users${qs ? `?${qs}` : ''}`;
    const response = await this.request<ApiResponse<PagedResult<User>>>(path);
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getAssignmentMatrix(search?: string): Promise<{
    users: User[];
    devices: Device[];
    assignments: Record<string, string[]>;
  }> {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    const qs = params.toString();
    const url = qs ? `/api/assignments?${qs}` : '/api/assignments';
    const response = await this.request<ApiResponse<{
      users: User[];
      devices: Device[];
      assignments: Record<string, string[]>;
    }>>(url);
    
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async bulkAssign(userIds: string[], deviceIds: string[]): Promise<{
    jobsCreated: number;
    jobIds: string[];
    skipped: number;
  }> {
    const response = await this.request<ApiResponse<{
      jobsCreated: number;
      jobIds: string[];
      skipped: number;
    }>>('/api/assignments/bulk-assign', {
      method: 'POST',
      body: JSON.stringify({ userIds, deviceIds }),
    });
    
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'ASSIGNMENT_FAILED');
  }

  async bulkUnassign(userIds: string[], deviceIds: string[]): Promise<{
    jobsCreated: number;
    jobIds: string[];
    skipped?: number;
  }> {
    const response = await this.request<ApiResponse<{
      jobsCreated: number;
      jobIds: string[];
      skipped?: number;
    }>>('/api/assignments/bulk-unassign', {
      method: 'POST',
      body: JSON.stringify({ userIds, deviceIds }),
    });
    
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'ASSIGNMENT_FAILED');
  }

  async getOrgDeviceLinks(params: {
    departmentId?: string;
    scheduleGroupId?: string;
    tenantId?: string;
  } = {}): Promise<
    Array<{
      linkId: string;
      tenantId: string;
      departmentId?: string | null;
      scheduleGroupId?: string | null;
      deviceId: string;
      deviceName?: string | null;
      isActive: boolean;
      createdAt: string;
    }>
  > {
    const qs = new URLSearchParams();
    if (params.departmentId) qs.set('departmentId', params.departmentId);
    if (params.scheduleGroupId) qs.set('scheduleGroupId', params.scheduleGroupId);
    if (params.tenantId) qs.set('tenantId', params.tenantId);
    const query = qs.toString();
    const response = await this.request<
      ApiResponse<
        Array<{
          linkId: string;
          tenantId: string;
          departmentId?: string | null;
          scheduleGroupId?: string | null;
          deviceId: string;
          deviceName?: string | null;
          isActive: boolean;
          createdAt: string;
        }>
      >
    >(query ? `/api/org-device-links?${query}` : '/api/org-device-links');
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getOrgDeviceLinksOverview(tenantId?: string): Promise<{
    activePolicyCount: number;
    departmentsWithLink: number;
    groupsWithLink: number;
    averageCoveragePercent: number;
    policies: Array<{
      kind: string;
      orgId: string;
      name: string;
      hasLink: boolean;
      devices: Array<{ deviceId: string; deviceName?: string | null }>;
      memberCount: number;
      alreadyOnAll: number;
      coveragePercent: number;
      policyUpdatedAt?: string | null;
      lastActor?: string | null;
    }>;
  }> {
    const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
    const response = await this.request<ApiResponse<{
      activePolicyCount: number;
      departmentsWithLink: number;
      groupsWithLink: number;
      averageCoveragePercent: number;
      policies: Array<{
        kind: string;
        orgId: string;
        name: string;
        hasLink: boolean;
        devices: Array<{ deviceId: string; deviceName?: string | null }>;
        memberCount: number;
        alreadyOnAll: number;
        coveragePercent: number;
        policyUpdatedAt?: string | null;
        lastActor?: string | null;
      }>;
    }>>(`/api/org-device-links/overview${qs}`);
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async previewDepartmentOrgDeviceLinks(
    departmentId: string,
    deviceIds: string[],
    tenantId?: string
  ): Promise<{
    kind: string;
    orgId: string;
    memberCount: number;
    assignJobsEstimate: number;
    unassignJobsEstimate: number;
    sampleAssignUsers: Array<{ userId: string; displayName: string }>;
    sampleUnassignUsers: Array<{ userId: string; displayName: string }>;
    policyUpdatedAt?: string | null;
    requiresDangerConfirm: boolean;
  }> {
    const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
    const response = await this.request<ApiResponse<{
      kind: string;
      orgId: string;
      memberCount: number;
      assignJobsEstimate: number;
      unassignJobsEstimate: number;
      sampleAssignUsers: Array<{ userId: string; displayName: string }>;
      sampleUnassignUsers: Array<{ userId: string; displayName: string }>;
      policyUpdatedAt?: string | null;
      requiresDangerConfirm: boolean;
    }>>(`/api/org-device-links/department/${encodeURIComponent(departmentId)}/preview${qs}`, {
      method: 'POST',
      body: JSON.stringify({ deviceIds }),
    });
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async previewGroupOrgDeviceLinks(
    scheduleGroupId: string,
    deviceIds: string[],
    tenantId?: string
  ): Promise<{
    kind: string;
    orgId: string;
    memberCount: number;
    assignJobsEstimate: number;
    unassignJobsEstimate: number;
    sampleAssignUsers: Array<{ userId: string; displayName: string }>;
    sampleUnassignUsers: Array<{ userId: string; displayName: string }>;
    policyUpdatedAt?: string | null;
    requiresDangerConfirm: boolean;
  }> {
    const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
    const response = await this.request<ApiResponse<{
      kind: string;
      orgId: string;
      memberCount: number;
      assignJobsEstimate: number;
      unassignJobsEstimate: number;
      sampleAssignUsers: Array<{ userId: string; displayName: string }>;
      sampleUnassignUsers: Array<{ userId: string; displayName: string }>;
      policyUpdatedAt?: string | null;
      requiresDangerConfirm: boolean;
    }>>(`/api/org-device-links/group/${encodeURIComponent(scheduleGroupId)}/preview${qs}`, {
      method: 'POST',
      body: JSON.stringify({ deviceIds }),
    });
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getOrgDeviceEffectiveAccess(
    userId: string,
    tenantId?: string
  ): Promise<{
    userId: string;
    displayName: string;
    devices: Array<{
      deviceId: string;
      deviceName?: string | null;
      sources: Array<{ kind: string; id?: string | null; name?: string | null }>;
    }>;
  }> {
    const qs = new URLSearchParams({ userId });
    if (tenantId) qs.set('tenantId', tenantId);
    const response = await this.request<ApiResponse<{
      userId: string;
      displayName: string;
      devices: Array<{
        deviceId: string;
        deviceName?: string | null;
        sources: Array<{ kind: string; id?: string | null; name?: string | null }>;
      }>;
    }>>(`/api/org-device-links/effective-access?${qs.toString()}`);
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async replaceDepartmentOrgDeviceLinks(
    departmentId: string,
    deviceIds: string[],
    tenantId?: string,
    ifMatchPolicyUpdatedAt?: string | null
  ): Promise<{
    linksActive: number;
    linksRemoved: number;
    policyUpdatedAt?: string;
    reconcile: {
      usersProcessed: number;
      assignJobsCreated: number;
      unassignJobsCreated: number;
      assignSkipped: number;
      unassignSkipped: number;
    };
  }> {
    const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
    const headers: Record<string, string> = {};
    if (ifMatchPolicyUpdatedAt === null) {
      headers['If-Match'] = 'empty';
    } else if (ifMatchPolicyUpdatedAt) {
      headers['If-Match'] = `"${ifMatchPolicyUpdatedAt}"`;
    }
    const response = await this.request<
      ApiResponse<{
        linksActive: number;
        linksRemoved: number;
        policyUpdatedAt?: string;
        reconcile: {
          usersProcessed: number;
          assignJobsCreated: number;
          unassignJobsCreated: number;
          assignSkipped: number;
          unassignSkipped: number;
        };
      }>
    >(`/api/org-device-links/department/${encodeURIComponent(departmentId)}${qs}`, {
      method: 'PUT',
      body: JSON.stringify({ deviceIds }),
      headers,
    });
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async replaceGroupOrgDeviceLinks(
    scheduleGroupId: string,
    deviceIds: string[],
    tenantId?: string,
    ifMatchPolicyUpdatedAt?: string | null
  ): Promise<{
    linksActive: number;
    linksRemoved: number;
    policyUpdatedAt?: string;
    reconcile: {
      usersProcessed: number;
      assignJobsCreated: number;
      unassignJobsCreated: number;
      assignSkipped: number;
      unassignSkipped: number;
    };
  }> {
    const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
    const headers: Record<string, string> = {};
    if (ifMatchPolicyUpdatedAt === null) {
      headers['If-Match'] = 'empty';
    } else if (ifMatchPolicyUpdatedAt) {
      headers['If-Match'] = `"${ifMatchPolicyUpdatedAt}"`;
    }
    const response = await this.request<
      ApiResponse<{
        linksActive: number;
        linksRemoved: number;
        policyUpdatedAt?: string;
        reconcile: {
          usersProcessed: number;
          assignJobsCreated: number;
          unassignJobsCreated: number;
          assignSkipped: number;
          unassignSkipped: number;
        };
      }>
    >(`/api/org-device-links/group/${encodeURIComponent(scheduleGroupId)}${qs}`, {
      method: 'PUT',
      body: JSON.stringify({ deviceIds }),
      headers,
    });
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async reconcileOrgDeviceAccess(
    body: { userId?: string; departmentId?: string; scheduleGroupId?: string },
    tenantId?: string
  ): Promise<{
    usersProcessed: number;
    assignJobsCreated: number;
    unassignJobsCreated: number;
  }> {
    const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
    const response = await this.request<
      ApiResponse<{
        usersProcessed: number;
        assignJobsCreated: number;
        unassignJobsCreated: number;
      }>
    >(`/api/org-device-links/reconcile${qs}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  // Sync Queue / Jobs
  async getSyncJobs(filters: {
    status?: string;
    commandType?: string;
    deviceId?: string;
    tenantId?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  } = {}): Promise<PagedResult<any>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const response = await this.request<ApiResponse<PagedResult<any>>>(`/api/sync/jobs?${params}`);
    
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getSyncStats(filters: {
    commandType?: string;
    tenantId?: string;
  } = {}): Promise<{
    pending: number;
    inProgress: number;
    completed24h: number;
    failed24h: number;
    failedTotal: number;
  }> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    const qs = params.toString();
    const response = await this.request<ApiResponse<{
      pending: number;
      inProgress: number;
      completed24h: number;
      failed24h: number;
      failedTotal: number;
    }>>(`/api/sync/stats${qs ? `?${qs}` : ''}`);
    
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async retrySyncJob(jobId: number): Promise<{
    newJobId: number;
    originalJobId: number;
    status: string;
  }> {
    const response = await this.request<ApiResponse<{
      newJobId: number;
      originalJobId: number;
      status: string;
    }>>(`/api/sync/jobs/${jobId}/retry`, {
      method: 'POST',
    });
    
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'SYNC_JOB_RETRY_FAILED');
  }

  // Sync User to Device
  async syncUserToDevice(deviceId: string, userId: string): Promise<number> {
    const response = await this.request<ApiResponse<number>>(`/api/devices/${deviceId}/sync-user`, {
      method: 'POST',
      body: JSON.stringify({ deviceId, userId }),
    });
    
    if (response.success && response.data !== undefined) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  // Tenant Admin APIs (SuperAdmin only)
  async getAdminTenants(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: string;
  } = {}): Promise<PagedResult<any>> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });
    const response = await this.request(`/api/admin/tenants?${queryParams}`) as any;
    return (response as any).data;
  }

  /** GET /api/worker — optional server-side search and SuperAdmin tenant filter */
  async getWorkers(params: { search?: string; tenantId?: string } = {}): Promise<WorkerClient[]> {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.tenantId) queryParams.append('tenantId', params.tenantId);
    const qs = queryParams.toString();
    const response = await this.request<ApiResponse<WorkerClient[]>>(`/api/worker${qs ? `?${qs}` : ''}`);
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  /** GET /api/worker/statistics — pass tenantId for SuperAdmin tenant-scoped stats */
  async getWorkerStatistics(tenantId?: string): Promise<WorkerStatistics> {
    const queryParams = new URLSearchParams();
    if (tenantId && tenantId !== 'all') {
      queryParams.append('tenantId', tenantId);
    }
    const qs = queryParams.toString();
    const response = await this.request<ApiResponse<WorkerStatistics>>(`/api/worker/statistics${qs ? `?${qs}` : ''}`);
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getWorkerOfflineIncidents(clientId: string): Promise<WorkerOfflineIncident[]> {
    const encoded = encodeURIComponent(clientId);
    const response = await this.request<ApiResponse<WorkerOfflineIncident[]>>(
      `/api/worker/${encoded}/offline-incidents`,
    );
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'RESOURCE_NOT_FOUND');
  }

  async getWorkerOfflineEmailSettings(): Promise<{ enabled: boolean }> {
    const response = await this.request<ApiResponse<{ enabled: boolean }>>('/api/worker/offline-email');
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'RESOURCE_NOT_FOUND');
  }

  async setWorkerOfflineEmailSettings(enabled: boolean): Promise<{ enabled: boolean }> {
    const response = await this.request<ApiResponse<{ enabled: boolean }>>('/api/worker/offline-email', {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'RESOURCE_NOT_FOUND');
  }

  async getWorkerById(clientId: string): Promise<WorkerClient> {
    const encoded = encodeURIComponent(clientId);
    const response = await this.request<ApiResponse<WorkerClient>>(`/api/worker/${encoded}`);
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'RESOURCE_NOT_FOUND');
  }

  async updateWorker(clientId: string, data: UpdateWorkerDto): Promise<WorkerClient> {
    const encoded = encodeURIComponent(clientId);
    const response = await this.request<ApiResponse<WorkerClient>>(`/api/worker/${encoded}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  /** SuperAdmin only — returns decrypted AnyDesk password. */
  async revealWorkerAnyDeskPassword(clientId: string): Promise<string> {
    const encoded = encodeURIComponent(clientId);
    const response = await this.request<ApiResponse<{ password: string }>>(
      `/api/worker/${encoded}/reveal-anydesk-password`,
      { method: 'POST', body: JSON.stringify({}) }
    );
    const pwd = response.data?.password;
    if (response.success && typeof pwd === 'string') {
      return pwd;
    }
    throwApiResponseError(response, 200, 'WORKER_REVEAL_PASSWORD_FAILED');
  }

  async getAdminTenantById(id: string): Promise<any> {
    const response = await this.request(`/api/admin/tenants/${id}`) as any;
    return (response as any).data;
  }

  async updateAdminTenant(id: string, data: {
    name: string;
    status: string;
    contactEmail?: string;
    description?: string;
  }): Promise<any> {
    const response = await this.request(`/api/admin/tenants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return (response as any).data;
  }




  async getAdminLogs(filters: {
    level?: string;
    tenantId?: string;
    timeRange?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  } = {}): Promise<PagedResult<any>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const response = await this.request<ApiResponse<PagedResult<any>>>(`/api/admin/logs?${params}`);
    
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  // Tenant Management (Admin only)
  async getTenants(page: number = 1, pageSize: number = 25, search?: string, status?: string): Promise<PagedResult<Tenant>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString()
    });
    
    if (search) params.append('search', search);
    if (status && status !== 'all') params.append('status', status);
    
    const response = await this.request<ApiResponse<PagedResult<Tenant>>>(`/api/tenant?${params}`);
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getTenant(tenantId: string): Promise<Tenant> {
    const response = await this.request<ApiResponse<Tenant>>(`/api/tenant/${tenantId}`);
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'RESOURCE_NOT_FOUND');
  }

  async createTenant(tenantData: Partial<Tenant>): Promise<Tenant> {
    const response = await this.request<ApiResponse<Tenant>>('/api/tenant', {
      method: 'POST',
      body: JSON.stringify(tenantData),
    });

    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'TENANT_CREATE_FAILED');
  }

  async updateTenant(tenantId: string, tenantData: Partial<Tenant>): Promise<Tenant> {
    const response = await this.request<ApiResponse<Tenant>>(`/api/tenant/${tenantId}`, {
      method: 'PUT',
      body: JSON.stringify(tenantData),
    });

    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'TENANT_UPDATE_FAILED');
  }

  async renewTenantApiKey(tenantId: string): Promise<Tenant> {
    const response = await this.request<ApiResponse<Tenant>>(`/api/tenant/${tenantId}/renew-api-key`, {
      method: 'POST',
    });

    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'TENANT_UPDATE_FAILED');
  }

  async getTenantStats(): Promise<TenantStats> {
    const response = await this.request<ApiResponse<TenantStats>>('/api/tenant/stats');
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  // Device Management
  async getDevices(filters: DeviceFilters = {}): Promise<PagedResult<Device>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const response = await this.request<ApiResponse<PagedResult<Device>>>(`/api/devicemanagement?${params}`);
    if (response.success && response.data) {
      return (response as any).data; // Return full PagedResult with totalCount
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getDevice(deviceId: string): Promise<Device> {
    const response = await this.request<ApiResponse<Device>>(`/api/devicemanagement/${deviceId}`);
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'RESOURCE_NOT_FOUND');
  }

  async getDeviceManagementStats(): Promise<{
    totalDevices: number;
    onlineDevices: number;
    offlineDevices: number;
    errorDevices: number;
    photoEnabledDevices: number;
  }> {
    const response = await this.request<
      ApiResponse<{
        totalDevices: number;
        onlineDevices: number;
        offlineDevices: number;
        errorDevices: number;
        photoEnabledDevices: number;
      }>
    >('/api/devicemanagement/stats');
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async createDevice(deviceData: Partial<Device>): Promise<Device> {
    const response = await this.request<ApiResponse<Device>>('/api/devicemanagement', {
      method: 'POST',
      body: JSON.stringify(deviceData),
    });

    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'DEVICE_CREATE_FAILED');
  }

  async updateDevice(deviceId: string, deviceData: Partial<Device>): Promise<ApiResponse<Device>> {
    const response = await this.request<ApiResponse<Device>>(`/api/devicemanagement/${deviceId}`, {
      method: 'PUT',
      body: JSON.stringify(deviceData),
    });

    if (response.success && response.data) {
      return response;
    }
    throwApiResponseError(response, 200, 'DEVICE_UPDATE_FAILED');
  }

  async deleteDevice(deviceId: string): Promise<ApiResponse<void>> {
    const response = await this.request<ApiResponse<void>>(`/api/devicemanagement/${deviceId}`, {
      method: 'DELETE',
    });

    return response;
  }

  async getDevicesByStatus(status: string): Promise<Device[]> {
    const response = await this.request<ApiResponse<Device[]>>(`/api/DeviceManagement/status/${status}`);
    
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getDevicesByLocation(location: string): Promise<Device[]> {
    const response = await this.request<ApiResponse<Device[]>>(`/api/DeviceManagement/location/${encodeURIComponent(location)}`);
    
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  // Attendance
  async getAttendances(filters: AttendanceFilters = {}): Promise<PagedResult<Attendance>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (value instanceof Date) {
          params.append(key, value.toISOString());
        } else {
          params.append(key, value.toString());
        }
      }
    });

    const response = await this.request<ApiResponse<PagedResult<Attendance>>>(`/api/attendance?${params}`);
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async createAttendance(attendanceData: AttendanceEventDto): Promise<Attendance> {
    const response = await this.request<ApiResponse<Attendance>>('/api/attendance', {
      method: 'POST',
      body: JSON.stringify(attendanceData),
    });

    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'ATTENDANCE_FIELDS_REQUIRED');
  }

  async getMyAttendance(from?: string, to?: string): Promise<{
    userId: string;
    from: string;
    to: string;
    punches: Array<{
      id: number;
      userId: string;
      timestamp: string;
      deviceId: string;
      eventType: string;
      displayEventType?: string;
      createdAt: string;
    }>;
  }> {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const qs = params.toString();
    const response = await this.request<ApiResponse<any>>(`/api/me/attendance${qs ? `?${qs}` : ''}`);
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getMySchedule(from?: string, to?: string): Promise<{
    userId: string;
    from: string;
    to: string;
    shifts: Array<{
      scheduledShiftId: string;
      shiftDate: string;
      templateId: string;
      snapshotTemplateName?: string;
      status: string;
      snapshotJson?: string;
      locationId?: string;
    }>;
    exceptions?: Array<{
      exceptionId: string;
      exceptionDate: string;
      type: string;
      leaveType?: string;
      status: string;
      overrideTemplateId?: string;
      notes?: string;
    }>;
  }> {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const qs = params.toString();
    const response = await this.request<ApiResponse<any>>(`/api/me/schedule${qs ? `?${qs}` : ''}`);
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getMyDay(date?: string): Promise<any> {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    const qs = params.toString();
    const response = await this.request<ApiResponse<any>>(`/api/me/day${qs ? `?${qs}` : ''}`);
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async selfPunch(
    eventType: 'IN' | 'OUT',
    timestampUtc?: string
  ): Promise<{ duplicate?: boolean; data?: any }> {
    const body: { eventType: string; timestamp?: string } = { eventType };
    if (timestampUtc) body.timestamp = timestampUtc;
    const response = await this.request<ApiResponse<any>>('/api/me/punch', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (response.success) {
      return {
        duplicate: !!(response as ApiResponse<any>).duplicate,
        data: response.data,
      };
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getMyAdjustments(from?: string, to?: string, status?: string): Promise<
    import('@/lib/types').AttendanceAdjustmentRequestDto[]
  > {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    if (status) params.append('status', status);
    const qs = params.toString();
    const response = await this.request<ApiResponse<import('@/lib/types').AttendanceAdjustmentRequestDto[]>>(
      `/api/me/adjustments${qs ? `?${qs}` : ''}`
    );
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async submitMyAdjustment(body: {
    workDate: string;
    targetEventType: 'IN' | 'OUT' | string;
    targetTimestampUtc: string;
    reason: string;
    adjustmentAction?: 'Create' | 'Update' | 'Delete';
    targetAttendanceId?: number;
  }): Promise<import('@/lib/types').AttendanceAdjustmentRequestDto> {
    const response = await this.request<ApiResponse<import('@/lib/types').AttendanceAdjustmentRequestDto>>(
      '/api/me/adjustments',
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getAttendance(id: number): Promise<Attendance> {
    const response = await this.request<ApiResponse<Attendance>>(`/api/attendance/${id}`);
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'RESOURCE_NOT_FOUND');
  }

  /** JPEG bytes with JWT + tenant header. Do not use as `<img src>`. Writes an admin photo access log. */
  async getAttendancePhotoBlob(id: number): Promise<Blob> {
    return this.fetchAttendanceJpeg(`/api/attendance/${id}/photo`);
  }

  /**
   * Approved live reference photograph for a punch detail. Any non-image response is null
   * so the punch frame stays as it is, without a missing-photo warning.
   */
  async getAttendanceReferencePhotoBlob(id: number): Promise<Blob | null> {
    try {
      return await this.fetchAttendanceJpeg(`/api/attendance/${id}/reference-photo`);
    } catch {
      return null;
    }
  }

  /** Day-grid JPEG. Same auth headers as getAttendancePhotoBlob. Does not write an access log. */
  async getAttendancePhotoPreviewBlob(id: number): Promise<Blob> {
    return this.fetchAttendanceJpeg(`/api/attendance/${id}/photo-preview`);
  }

  private async fetchAttendanceJpeg(endpoint: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (typeof window !== 'undefined') {
      const token = getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
      const tenantId = getStoredTenantId();
      if (tenantId && !isTenantScopeExcludedPath(window.location.pathname)) {
        headers['X-Tenant-ID'] = tenantId;
      }
    }
    headers['X-Correlation-ID'] = this.generateCorrelationId();

    const response = await fetch(`${this.baseUrl}${endpoint}`, { headers, credentials: 'include' });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw Object.assign(new Error(parseApiErrorMessage(errorData, response.status)), {
        status: response.status,
      });
    }
    return response.blob();
  }

  async reportAttendancePhotoMismatch(id: number): Promise<{
    attendanceId: number;
    userId: string;
    workDate: string;
    retentionExempt: boolean;
  }> {
    const response = await this.request<
      ApiResponse<{
        attendanceId: number;
        userId: string;
        workDate: string;
        retentionExempt: boolean;
      }>
    >(`/api/attendance/${id}/report-mismatch`, { method: 'POST' });
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getMyAdjustmentPhotoBlob(adjustmentId: string): Promise<Blob> {
    const endpoint = `/api/me/adjustments/${encodeURIComponent(adjustmentId)}/photo`;
    const headers: Record<string, string> = {};
    if (typeof window !== 'undefined') {
      const token = getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
      const tenantId = getStoredTenantId();
      if (tenantId && !isTenantScopeExcludedPath(window.location.pathname)) {
        headers['X-Tenant-ID'] = tenantId;
      }
    }
    headers['X-Correlation-ID'] = this.generateCorrelationId();

    const response = await fetch(`${this.baseUrl}${endpoint}`, { headers, credentials: 'include' });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw Object.assign(new Error(parseApiErrorMessage(errorData, response.status)), {
        status: response.status,
      });
    }
    return response.blob();
  }

  async updateAttendance(id: number, attendanceData: AttendanceEventDto): Promise<Attendance> {
    const response = await this.request<ApiResponse<Attendance>>(`/api/attendance/${id}`, {
      method: 'PUT',
      body: JSON.stringify(attendanceData),
    });

    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async deleteAttendance(id: number): Promise<void> {
    const response = await this.request<ApiResponse<object>>(`/api/attendance/${id}`, {
      method: 'DELETE',
    });

    if (!response.success) {
      throwApiResponseError(response, 200, 'OPERATION_FAILED');
    }
  }

  async processBulkAttendance(events: AttendanceEventDto[]): Promise<any> {
    const response = await this.request<ApiResponse<any>>('/api/attendance/bulk', {
      method: 'POST',
      body: JSON.stringify({ events }),
    });

    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'ATTENDANCE_INVALID_BULK_DATA');
  }

  async getAttendanceStatistics(fromDate?: Date, toDate?: Date): Promise<any> {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate.toISOString());
    if (toDate) params.append('toDate', toDate.toISOString());
    
    const response = await this.request<ApiResponse<any>>(`/api/attendance/statistics?${params}`);
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getAttendanceAnalytics(timeframe: string = 'lastWeek'): Promise<any> {
    const response = await this.request<ApiResponse<any>>(`/api/attendance/analytics?timeframe=${timeframe}`);
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getRealTimeAttendanceStatus(): Promise<any> {
    const response = await this.request<ApiResponse<any>>('/api/attendance/realtime');
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async exportAttendanceData(exportRequest: any): Promise<any> {
    const response = await this.request<ApiResponse<any>>('/api/attendance/export', {
      method: 'POST',
      body: JSON.stringify(exportRequest),
    });

    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  // Dashboard
  async getDashboardMetrics(): Promise<DashboardMetrics> {
    const response = await this.request<ApiResponse<DashboardMetrics>>('/api/dashboard/metrics');
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getTenantMetrics(tenantId: string): Promise<TenantMetrics> {
    const response = await this.request<ApiResponse<TenantMetrics>>(`/api/tenants/${tenantId}/metrics`);
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  // Auth methods
  async register(credentials: RegisterCredentials): Promise<ApiResponse<LoginResponse>> {
    const response = await this.request<ApiResponse<LoginResponse>>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    if (response.success && response.data) {
      persistAuthSession({
        token: response.data.token,
        user: response.data.user,
        tenantId: response.data.tenantId,
      });
      return response;
    }

    throwApiResponseError(response, 200, 'REGISTRATION_FAILED');
  }

  async getAvailableTenants(): Promise<TenantInfo[]> {
    const response = await this.request<ApiResponse<TenantInfo[]>>('/api/auth/tenants', {
      method: 'GET',
    });

    if (response.success && response.data) {
      return (response as any).data;
    }

    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  // Command queue actions (list via /api/sync; worker uses /api/command/pending + status)
  async cancelCommand(commandId: number): Promise<any> {
    const response = await this.request<ApiResponse<any>>(`/api/command/${commandId}/cancel`, {
      method: 'PUT',
    });

    if (response.success) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async queueRefreshDevicesCommand(tenantId: string): Promise<any> {
    const response = await this.request<ApiResponse<any>>('/api/command/refresh-devices', {
      method: 'POST',
      body: JSON.stringify({ tenantId }),
    });

    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async queueSyncDeviceTimesCommand(tenantId: string): Promise<{ jobsCreated: number; deviceIds: string[] }> {
    const response = await this.request<ApiResponse<{ jobsCreated: number; deviceIds: string[] }>>(
      '/api/command/sync-device-times',
      {
        method: 'POST',
        body: JSON.stringify({ tenantId }),
      }
    );

    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async queueSyncDeviceTimeCommand(
    deviceId: string
  ): Promise<{ syncSuccessful: boolean; message: string }> {
    const response = await this.request<
      ApiResponse<{
        deviceId: string;
        syncSuccessful: boolean;
        message: string;
      }>
    >(`/api/devicemanagement/${encodeURIComponent(deviceId)}/sync-time`, {
      method: 'POST',
    });

    if (response.success && response.data) {
      return {
        syncSuccessful: response.data.syncSuccessful,
        message: response.message ?? response.data.message ?? '',
      };
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async queueTerminalUsers(deviceId: string): Promise<{ commandId: number }> {
    const response = await this.request<ApiResponse<{ commandId: number }>>(
      `/api/devicemanagement/${encodeURIComponent(deviceId)}/terminal-users`,
      { method: 'POST' }
    );

    if (response.success && response.data?.commandId) {
      return { commandId: response.data.commandId };
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getTerminalUsers(
    deviceId: string,
    commandId: number
  ): Promise<{
    commandId: number;
    status: string;
    message?: string | null;
    users: Array<{
      userId: string;
      name: string;
      cardNumber?: string | null;
      privilege: number;
      enabled: boolean;
    }>;
  }> {
    const response = await this.request<
      ApiResponse<{
        commandId: number;
        status: string;
        message?: string | null;
        users?: Array<{
          userId: string;
          name: string;
          cardNumber?: string | null;
          privilege: number;
          enabled: boolean;
        }>;
      }>
    >(`/api/devicemanagement/${encodeURIComponent(deviceId)}/terminal-users/${commandId}`);

    if (response.success && response.data) {
      return {
        commandId: response.data.commandId,
        status: response.data.status,
        message: response.data.message,
        users: response.data.users ?? [],
      };
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async cleanupOldCommands(tenantId: string): Promise<any> {
    const response = await this.request<ApiResponse<any>>(`/api/command/cleanup?tenantId=${encodeURIComponent(tenantId)}`, {
      method: 'POST',
    });

    if (response.success) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }



  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await this.request<{ status: string; timestamp: string }>('/api/health');
    return response;
  }

  // Configuration API
  async getRealTimeConfig(): Promise<RealTimeConfig> {
    const response = await this.request<ApiResponse<RealTimeConfig>>('/api/configuration/realtime');
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async getRealTimeStatus(): Promise<RealTimeStatus> {
    const response = await this.request<ApiResponse<RealTimeStatus>>('/api/configuration/realtime/status');
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  // Tenant Features API (SuperAdmin only)
  async getTenantFeatures(tenantId: string): Promise<TenantFeatures> {
    const response = await this.request<ApiResponse<TenantFeatures>>(`/api/tenantfeatures/${tenantId}`);
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  // Tenant Management API (SuperAdmin only)
  async addTenantAdmin(tenantId: string, adminData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    phone?: string;
    department?: string;
    position?: string;
    employeeId?: string;
    jmbg?: string;
    cardNumber?: string;
  }): Promise<void> {
    const response = await this.request<ApiResponse<void>>(`/api/tenant/${tenantId}/add-admin`, {
      method: 'POST',
      body: JSON.stringify(adminData),
    });
    if (!response.success) {
      throwApiResponseError(response, 200, 'USER_CREATE_FAILED');
    }
  }

  async deleteTenant(tenantId: string): Promise<void> {
    const response = await this.request<ApiResponse<void>>(`/api/tenant/${tenantId}`, {
      method: 'DELETE',
    });
    if (!response.success) {
      throwApiResponseError(response, 200, 'TENANT_DELETE_FAILED');
    }
  }

  async updateTenantFeatures(
    tenantId: string,
    features: TenantFeatures,
    extras?: { attendancePhotoAttestationAcknowledged?: boolean }
  ): Promise<TenantFeatures> {
    const response = await this.request<ApiResponse<TenantFeatures>>(`/api/tenantfeatures/${tenantId}`, {
      method: 'PUT',
      body: JSON.stringify({
        ...features,
        ...(extras?.attendancePhotoAttestationAcknowledged
          ? { attendancePhotoAttestationAcknowledged: true }
          : {}),
      }),
    });
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'TENANT_UPDATE_FAILED');
  }

  async getCurrentTenantFeatures(): Promise<TenantFeatures> {
    const response = await this.request<ApiResponse<TenantFeatures>>('/api/tenantfeatures/current');
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  // User Settings API
  async getUserSettings(): Promise<UserSettings> {
    const response = await this.request<ApiResponse<UserSettings>>('/api/usersettings');
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'SETTINGS_LOAD_FAILED');
  }

  async updateUserSettings(settings: UpdateUserSettings): Promise<UserSettings> {
    const response = await this.request<ApiResponse<UserSettings>>('/api/usersettings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'SETTINGS_SAVE_FAILED');
  }

  async resetUserSettings(): Promise<UserSettings> {
    const response = await this.request<ApiResponse<UserSettings>>('/api/usersettings/reset', {
      method: 'POST',
    });
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'SETTINGS_RESET_FAILED');
  }

  async getUserRealTimeSettings(): Promise<{ enableLiveDeviceStatus: boolean; enableLiveAttendance: boolean; enableSignalR: boolean; enableSyncQueueRealtime: boolean }> {
    const response = await this.request<ApiResponse<{ enableLiveDeviceStatus: boolean; enableLiveAttendance: boolean; enableSignalR: boolean; enableSyncQueueRealtime: boolean }>>('/api/usersettings/realtime');
    if (response.success && response.data) {
      return (response as any).data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  // ==================== Department Management APIs ====================

  /**
   * Get departments for current tenant (paginated)
   */
  async getDepartments(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    isActive?: boolean;
    tenantId?: string; // SuperAdmin can filter by tenant
  }): Promise<DepartmentPagedResult> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params.tenantId) queryParams.append('tenantId', params.tenantId);

    const response = await this.request<ApiResponse<DepartmentPagedResult>>(
      `/api/departments?${queryParams.toString()}`
    );
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  /**
   * Get all departments for dropdown (no pagination)
   */
  async getAllDepartments(params?: {
    activeOnly?: boolean;
    tenantId?: string; // SuperAdmin can specify tenant
  }): Promise<DepartmentListItem[]> {
    const queryParams = new URLSearchParams();
    if (params?.activeOnly !== undefined) queryParams.append('activeOnly', params.activeOnly.toString());
    if (params?.tenantId) queryParams.append('tenantId', params.tenantId);

    const response = await this.request<ApiResponse<DepartmentListItem[]>>(
      `/api/departments/all?${queryParams.toString()}`
    );
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  /**
   * Get department by ID
   */
  async getDepartmentById(departmentId: string): Promise<Department> {
    const response = await this.request<ApiResponse<Department>>(`/api/departments/${departmentId}`);
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'RESOURCE_NOT_FOUND');
  }

  /**
   * Create new department
   */
  async createDepartment(data: CreateDepartmentDto): Promise<Department> {
    const response = await this.request<ApiResponse<Department>>('/api/departments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'DEPARTMENT_CREATE_FAILED');
  }

  /**
   * Update department
   */
  async updateDepartment(departmentId: string, data: UpdateDepartmentDto): Promise<Department> {
    const response = await this.request<ApiResponse<Department>>(`/api/departments/${departmentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'DEPARTMENT_UPDATE_FAILED');
  }

  /**
   * Delete department. When users are assigned, pass reassignToDepartmentId (null clears membership).
   */
  async deleteDepartment(
    departmentId: string,
    options?: { reassignToDepartmentId?: string | null }
  ): Promise<boolean> {
    const params = new URLSearchParams();
    if (options && 'reassignToDepartmentId' in options) {
      params.set('reassignToDepartmentId', options.reassignToDepartmentId ?? '');
    }
    const qs = params.toString();
    const path = `/api/departments/${encodeURIComponent(departmentId)}${qs ? `?${qs}` : ''}`;
    const response = await this.request<ApiResponse<boolean>>(path, {
      method: 'DELETE',
    });
    if (response.success) {
      return true;
    }
    throwApiResponseError(response, 200, 'DEPARTMENT_DELETE_FAILED');
  }

  // Integration clients (SuperAdmin only)
  async listIntegrationClients(): Promise<IntegrationClientListItem[]> {
    const response = await this.request<ApiResponse<IntegrationClientListItem[]>>(
      '/api/admin/integrations'
    );
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async createIntegrationClient(
    body: CreateIntegrationClientRequest
  ): Promise<CreateIntegrationClientResponse> {
    const response = await this.request<ApiResponse<CreateIntegrationClientResponse>>(
      '/api/admin/integrations',
      { method: 'POST', body: JSON.stringify(body) }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async patchIntegrationClient(
    clientId: string,
    body: PatchIntegrationClientRequest
  ): Promise<IntegrationClientListItem> {
    const encoded = encodeURIComponent(clientId);
    const response = await this.request<ApiResponse<IntegrationClientListItem>>(
      `/api/admin/integrations/${encoded}`,
      { method: 'PATCH', body: JSON.stringify(body) }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async deleteIntegrationClient(clientId: string): Promise<void> {
    const encoded = encodeURIComponent(clientId);
    const response = await this.request<ApiResponse<object>>(`/api/admin/integrations/${encoded}`, {
      method: 'DELETE',
    });
    if (!response.success) {
      throwApiResponseError(response, 200, 'INTEGRATION_CLIENT_NOT_FOUND');
    }
  }

  async addIntegrationTenant(clientId: string, tenantId: string): Promise<void> {
    const encoded = encodeURIComponent(clientId);
    const response = await this.request<ApiResponse<object>>(
      `/api/admin/integrations/${encoded}/tenants`,
      { method: 'POST', body: JSON.stringify({ tenantId }) }
    );
    if (!response.success) {
      throwApiResponseError(response, 200, 'INTEGRATION_TENANT_FORBIDDEN');
    }
  }

  async removeIntegrationTenant(clientId: string, tenantId: string): Promise<void> {
    const encodedClient = encodeURIComponent(clientId);
    const encodedTenant = encodeURIComponent(tenantId);
    const response = await this.request<ApiResponse<object>>(
      `/api/admin/integrations/${encodedClient}/tenants/${encodedTenant}`,
      { method: 'DELETE' }
    );
    if (!response.success) {
      throwApiResponseError(response, 200, 'OPERATION_FAILED');
    }
  }

  async getIntegrationApiKey(clientId: string): Promise<RotateIntegrationKeyResponse> {
    const encoded = encodeURIComponent(clientId);
    const response = await this.request<ApiResponse<RotateIntegrationKeyResponse>>(
      `/api/admin/integrations/${encoded}/api-key`
    );
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  async rotateIntegrationKey(clientId: string): Promise<RotateIntegrationKeyResponse> {
    const encoded = encodeURIComponent(clientId);
    const response = await this.request<ApiResponse<RotateIntegrationKeyResponse>>(
      `/api/admin/integrations/${encoded}/rotate-key`,
      { method: 'POST' }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throwApiResponseError(response, 200, 'OPERATION_FAILED');
  }

  // --- Feedback Hub ---

  async createFeedback(data: CreateFeedbackRequest): Promise<FeedbackDetail> {
    const response = await this.request<ApiResponse<FeedbackDetail>>('/api/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'FEEDBACK_CREATE_FAILED');
  }

  async getMyFeedback(params: {
    page?: number;
    pageSize?: number;
    type?: string;
    status?: string;
    severity?: string;
    search?: string;
  } = {}): Promise<FeedbackPagedResult> {
    const q = new URLSearchParams();
    if (params.page) q.set('page', String(params.page));
    if (params.pageSize) q.set('pageSize', String(params.pageSize));
    if (params.type) q.set('type', params.type);
    if (params.status) q.set('status', params.status);
    if (params.severity) q.set('severity', params.severity);
    if (params.search) q.set('search', params.search);
    const response = await this.request<ApiResponse<FeedbackPagedResult>>(
      `/api/feedback/mine?${q.toString()}`
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'FEEDBACK_LIST_FAILED');
  }

  /** Tenant Feature/Improvement board (for discovery + company upvote). */
  async getTenantFeedbackIdeas(params: {
    page?: number;
    pageSize?: number;
    type?: string;
    status?: string;
    search?: string;
  } = {}): Promise<FeedbackPagedResult> {
    const q = new URLSearchParams();
    if (params.page) q.set('page', String(params.page));
    if (params.pageSize) q.set('pageSize', String(params.pageSize));
    if (params.type) q.set('type', params.type);
    if (params.status) q.set('status', params.status);
    if (params.search) q.set('search', params.search);
    const response = await this.request<ApiResponse<FeedbackPagedResult>>(
      `/api/feedback/ideas?${q.toString()}`
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'FEEDBACK_LIST_FAILED');
  }

  async getFeedback(id: string): Promise<FeedbackDetail> {
    const encoded = encodeURIComponent(id);
    const response = await this.request<ApiResponse<FeedbackDetail>>(
      `/api/feedback/${encoded}`
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'FEEDBACK_GET_FAILED');
  }

  async addFeedbackComment(
    id: string,
    body: string,
    isInternal = false
  ): Promise<FeedbackComment> {
    const encoded = encodeURIComponent(id);
    const response = await this.request<ApiResponse<FeedbackComment>>(
      `/api/feedback/${encoded}/comments`,
      { method: 'POST', body: JSON.stringify({ body, isInternal }) }
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'FEEDBACK_COMMENT_FAILED');
  }

  async reopenFeedback(id: string, comment: string): Promise<FeedbackDetail> {
    const encoded = encodeURIComponent(id);
    const response = await this.request<ApiResponse<FeedbackDetail>>(
      `/api/feedback/${encoded}/reopen`,
      { method: 'POST', body: JSON.stringify({ comment }) }
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'FEEDBACK_REOPEN_FAILED');
  }

  async uploadFeedbackAttachment(id: string, file: File): Promise<FeedbackAttachment> {
    const encoded = encodeURIComponent(id);
    const form = new FormData();
    form.append('file', file);
    const response = await this.request<ApiResponse<FeedbackAttachment>>(
      `/api/feedback/${encoded}/attachments`,
      { method: 'POST', body: form }
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'FEEDBACK_ATTACHMENT_UPLOAD_FAILED');
  }

  async downloadFeedbackAttachment(feedbackId: string, attachmentId: string, fileName: string): Promise<void> {
    const encodedFeedback = encodeURIComponent(feedbackId);
    const encodedAttachment = encodeURIComponent(attachmentId);
    const endpoint = `/api/feedback/${encodedFeedback}/attachments/${encodedAttachment}/content`;
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {};
    if (typeof window !== 'undefined') {
      const token = getAccessToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const tenantId = getStoredTenantId();
      if (tenantId && !isTenantScopeExcludedPath(window.location.pathname)) {
        headers['X-Tenant-ID'] = tenantId;
      }
    }
    headers['X-Correlation-ID'] = this.generateCorrelationId();

    const response = await fetch(url, { headers, credentials: 'include' });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(parseApiErrorMessage(errorData, response.status));
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName || 'attachment';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  }

  async deleteFeedbackAttachment(feedbackId: string, attachmentId: string): Promise<void> {
    const encodedFeedback = encodeURIComponent(feedbackId);
    const encodedAttachment = encodeURIComponent(attachmentId);
    const response = await this.request<ApiResponse<object>>(
      `/api/feedback/${encodedFeedback}/attachments/${encodedAttachment}`,
      { method: 'DELETE' }
    );
    if (!response.success) {
      throwApiResponseError(response, 200, 'FEEDBACK_ATTACHMENT_DELETE_FAILED');
    }
  }

  async getAdminFeedback(params: {
    page?: number;
    pageSize?: number;
    type?: string;
    status?: string;
    severity?: string;
    search?: string;
    tenantId?: string;
  } = {}): Promise<FeedbackPagedResult> {
    const q = new URLSearchParams();
    if (params.page) q.set('page', String(params.page));
    if (params.pageSize) q.set('pageSize', String(params.pageSize));
    if (params.type) q.set('type', params.type);
    if (params.status) q.set('status', params.status);
    if (params.severity) q.set('severity', params.severity);
    if (params.search) q.set('search', params.search);
    if (params.tenantId) q.set('tenantId', params.tenantId);
    const response = await this.request<ApiResponse<FeedbackPagedResult>>(
      `/api/admin/feedback?${q.toString()}`
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'FEEDBACK_LIST_FAILED');
  }

  async getPlatformFeedback(params: {
    page?: number;
    pageSize?: number;
    type?: string;
    status?: string;
    severity?: string;
    tenantId?: string;
    moduleArea?: string;
    search?: string;
  } = {}): Promise<FeedbackPagedResult> {
    const q = new URLSearchParams();
    if (params.page) q.set('page', String(params.page));
    if (params.pageSize) q.set('pageSize', String(params.pageSize));
    if (params.type) q.set('type', params.type);
    if (params.status) q.set('status', params.status);
    if (params.severity) q.set('severity', params.severity);
    if (params.tenantId) q.set('tenantId', params.tenantId);
    if (params.moduleArea) q.set('moduleArea', params.moduleArea);
    if (params.search) q.set('search', params.search);
    const response = await this.request<ApiResponse<FeedbackPagedResult>>(
      `/api/platform/feedback?${q.toString()}`
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'FEEDBACK_LIST_FAILED');
  }

  async patchPlatformFeedback(
    id: string,
    data: PatchFeedbackRequest
  ): Promise<FeedbackDetail> {
    const encoded = encodeURIComponent(id);
    const response = await this.request<ApiResponse<FeedbackDetail>>(
      `/api/platform/feedback/${encoded}`,
      { method: 'PATCH', body: JSON.stringify(data) }
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'FEEDBACK_PATCH_FAILED');
  }

  async voteFeedback(id: string): Promise<FeedbackDetail> {
    const encoded = encodeURIComponent(id);
    const response = await this.request<ApiResponse<FeedbackDetail>>(
      `/api/feedback/${encoded}/vote`,
      { method: 'POST' }
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'FEEDBACK_VOTE_FAILED');
  }

  async unvoteFeedback(id: string): Promise<FeedbackDetail> {
    const encoded = encodeURIComponent(id);
    const response = await this.request<ApiResponse<FeedbackDetail>>(
      `/api/feedback/${encoded}/vote`,
      { method: 'DELETE' }
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'FEEDBACK_UNVOTE_FAILED');
  }

  async submitFeedbackCsat(
    id: string,
    score: number,
    comment?: string
  ): Promise<FeedbackDetail> {
    const encoded = encodeURIComponent(id);
    const response = await this.request<ApiResponse<FeedbackDetail>>(
      `/api/feedback/${encoded}/csat`,
      { method: 'POST', body: JSON.stringify({ score, comment }) }
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'FEEDBACK_CSAT_FAILED');
  }

  async postHelpAnalyticsEvents(
    events: Array<{
      eventType: string;
      articleId?: string;
      tourId?: string;
      stepIndex?: number;
      payloadJson?: string;
    }>
  ): Promise<void> {
    const response = await this.request<ApiResponse<object>>('/api/help/analytics/events', {
      method: 'POST',
      body: JSON.stringify({ events }),
    });
    if (!response.success) throwApiResponseError(response, 200, 'HELP_ANALYTICS_FAILED');
  }

  async getHelpArticleCsat(articleId: string): Promise<HelpArticleCsat> {
    const encoded = encodeURIComponent(articleId);
    const response = await this.request<ApiResponse<HelpArticleCsat>>(
      `/api/help/articles/${encoded}/csat`
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'HELP_CSAT_GET_FAILED');
  }

  async upsertHelpArticleCsat(
    articleId: string,
    data: { helpful: boolean; rating?: number; comment?: string }
  ): Promise<HelpArticleCsat> {
    const encoded = encodeURIComponent(articleId);
    const response = await this.request<ApiResponse<HelpArticleCsat>>(
      `/api/help/articles/${encoded}/csat`,
      { method: 'POST', body: JSON.stringify(data) }
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'HELP_CSAT_SAVE_FAILED');
  }

  async getPlatformHelpAnalytics(params: {
    fromUtc?: string;
    toUtc?: string;
  } = {}): Promise<PlatformHelpAnalytics> {
    const q = new URLSearchParams();
    if (params.fromUtc) q.set('fromUtc', params.fromUtc);
    if (params.toUtc) q.set('toUtc', params.toUtc);
    const response = await this.request<ApiResponse<PlatformHelpAnalytics>>(
      `/api/platform/help/analytics?${q.toString()}`
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'HELP_ANALYTICS_GET_FAILED');
  }

  async getPlatformFeedbackAnalytics(params: {
    fromUtc?: string;
    toUtc?: string;
  } = {}): Promise<PlatformFeedbackAnalytics> {
    const q = new URLSearchParams();
    if (params.fromUtc) q.set('fromUtc', params.fromUtc);
    if (params.toUtc) q.set('toUtc', params.toUtc);
    const response = await this.request<ApiResponse<PlatformFeedbackAnalytics>>(
      `/api/platform/feedback/analytics?${q.toString()}`
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'FEEDBACK_ANALYTICS_GET_FAILED');
  }

  async getFleet(query: import('./fleet-types').FleetQuery = {}): Promise<import('./fleet-types').FleetList> {
    const q = new URLSearchParams();
    if (query.view) q.set('view', query.view);
    if (query.search) q.set('search', query.search);
    if (query.tenantIds && query.tenantIds.length > 0) q.set('tenantIds', query.tenantIds.join(','));
    if (query.state) q.set('state', query.state);
    if (query.version) q.set('version', query.version);
    if (query.showSimulators) q.set('showSimulators', 'true');
    if (query.errorsOnly) q.set('errorsOnly', 'true');
    const response = await this.request<ApiResponse<import('./fleet-types').FleetList>>(
      `/api/platform/fleet?${q.toString()}`
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'FLEET_GET_FAILED');
  }

  async getFleetWorker(clientId: string): Promise<import('./fleet-types').FleetWorkerDetail> {
    const response = await this.request<ApiResponse<import('./fleet-types').FleetWorkerDetail>>(
      `/api/platform/fleet/${encodeURIComponent(clientId)}`
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'FLEET_WORKER_GET_FAILED');
  }

  async saveFleetCurrentVersion(currentVersion: string): Promise<string | null> {
    const response = await this.request<ApiResponse<{ currentVersion?: string | null }>>(
      '/api/platform/fleet/current-version',
      { method: 'PUT', body: JSON.stringify({ currentVersion }) }
    );
    if (response.success && response.data) return response.data.currentVersion ?? null;
    throwApiResponseError(response, 200, 'FLEET_VERSION_SAVE_FAILED');
  }

  async getFleetEvents(clientId: string, hours: number, search?: string): Promise<import('./fleet-types').FleetEventRow[]> {
    const q = new URLSearchParams();
    q.set('hours', String(hours));
    if (search) q.set('search', search);
    const response = await this.request<ApiResponse<import('./fleet-types').FleetEventRow[]>>(
      `/api/platform/fleet/${encodeURIComponent(clientId)}/events?${q.toString()}`
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'FLEET_EVENTS_GET_FAILED');
  }

  async getFleetLog(clientId: string, includeBody: boolean): Promise<import('./fleet-types').FleetLogFetch | null> {
    const response = await this.request<ApiResponse<import('./fleet-types').FleetLogFetch | null>>(
      `/api/platform/fleet/${encodeURIComponent(clientId)}/log?includeBody=${includeBody ? 'true' : 'false'}`
    );
    if (response.success) return response.data ?? null;
    throwApiResponseError(response, 200, 'FLEET_LOG_GET_FAILED');
  }

  async requestFleetLog(clientId: string, fromUtc: string, toUtc: string, level: string): Promise<import('./fleet-types').FleetLogFetch> {
    const response = await this.request<ApiResponse<import('./fleet-types').FleetLogFetch>>(
      `/api/platform/fleet/${encodeURIComponent(clientId)}/log`,
      { method: 'POST', body: JSON.stringify({ fromUtc, toUtc, level }) }
    );
    if (response.success && response.data) return response.data;
    throwApiResponseError(response, 200, 'FLEET_LOG_REQUEST_FAILED');
  }

  async cancelFleetLog(clientId: string): Promise<void> {
    await this.request(`/api/platform/fleet/${encodeURIComponent(clientId)}/log/cancel`, { method: 'POST' });
  }

  async downloadFleetLog(clientId: string): Promise<string> {
    const response = await this.request<ApiResponse<import('./fleet-types').FleetLogFetch>>(
      `/api/platform/fleet/${encodeURIComponent(clientId)}/log/file`
    );
    if (response.success && response.data) return response.data.excerpt ?? '';
    throwApiResponseError(response, 200, 'FLEET_LOG_DOWNLOAD_FAILED');
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
